import React, { useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  registerables,
  type ChartOptions,
  type Plugin,
  type TooltipItem,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import WidgetFrame from "./WidgetFrame";
import EmptyRow from "./EmptyRow";
import { SkeletonChart } from "../Skeleton";
import Select from "../ReusableComponents/Select";
import { OVERLAY_COLORS, readSwatch } from "../../utils/colorUtils";
import {
  comparisonWindow,
  extrasInWindow,
  type GenerationComparison,
  type GenerationPair,
  type Kpi,
} from "../../utils/operationsDashboard";

ChartJS.register(...registerables);

// A per-chart plugin draws the divider; the annotation plugin would have to register globally, reaching every other chart
const nowDivider: Plugin<"line"> = {
  id: "operationsNowDivider",
  afterDatasetsDraw(chart) {
    const scale = chart.scales?.x;
    if (!scale) return;
    const now = Date.now();
    if (now < scale.min || now > scale.max) return;

    const { ctx, chartArea } = chart;
    const x = scale.getPixelForValue(now);
    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = readSwatch("text-eai-ink-subtle");
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

interface GenerationForecastWidgetProps {
  comparison: GenerationComparison | null;
  /** The comparison query's rejection — a configured pair whose data would not load reads differently from no pair at all. */
  error?: unknown;
  isLoading?: boolean;
  /** Every composed forecast that pairs a model against an actual feed — the picker's options. */
  pairs?: GenerationPair[];
  /** null is the freshest pair, which is what the board opens on. */
  pairId?: string | null;
  onPairChange?: (id: string | null) => void;
  /** The generation figure this chart is a picture of — it used to be a tile on the row above. */
  headline?: Kpi | null;
  className?: string;
}

const GenerationForecastWidget: React.FC<GenerationForecastWidgetProps> = ({
  comparison,
  error,
  isLoading,
  pairs = [],
  pairId = null,
  onPairChange,
  headline = null,
  className,
}) => {
  // Swatches read at render, not module scope (readSwatch needs the stylesheet); the pair wears DS tokens, extras OVERLAY_COLORS
  const chartData = useMemo(() => {
    if (!comparison) return null;
    return {
      datasets: [
        {
          label: comparison.actualLabel,
          data: comparison.actual.map((point) => ({ x: point.at, y: point.value })),
          borderColor: readSwatch("text-eai-cta-primary"),
          backgroundColor: "transparent",
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.15,
        },
        {
          label: comparison.forecastLabel,
          data: comparison.forecast.map((point) => ({ x: point.at, y: point.value })),
          borderColor: readSwatch("text-eai-ink-faint"),
          backgroundColor: "transparent",
          borderWidth: 1.5,
          borderDash: [4, 4],
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.15,
        },
        // Thinner and on their own axis: they are other quantities, and sharing the pair's scale would flatten it
        ...comparison.extras.map((extra, index) => ({
          label: extra.label,
          data: extra.points.map((point) => ({ x: point.at, y: point.value })),
          borderColor: OVERLAY_COLORS[index % OVERLAY_COLORS.length],
          backgroundColor: "transparent",
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.15,
          yAxisID: "yExtra",
        })),
      ],
    };
  }, [comparison]);

  // The pair owns the axis; an extra outside this span is drawn but simply falls off the end of it
  const xRange = useMemo(() => comparisonWindow(comparison), [comparison]);

  const options = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          align: "start",
          labels: {
            boxWidth: 10,
            boxHeight: 2,
            usePointStyle: false,
            color: readSwatch("text-eai-ink-subtle"),
            font: { family: "Inter", size: 11 },
            padding: 16,
          },
        },
        tooltip: {
          callbacks: {
            title: (items: TooltipItem<"line">[]) => {
              const x = items[0]?.parsed.x;
              return x == null ? "" : new Date(x).toLocaleString();
            },
            label: (item: TooltipItem<"line">) =>
              `${item.dataset.label}: ${item.parsed.y?.toFixed(2)}${
                comparison?.unit ? ` ${comparison.unit}` : ""
              }`,
          },
        },
      },
      scales: {
        x: {
          type: "time",
          min: xRange?.min,
          max: xRange?.max,
          grid: { display: false },
          border: { color: readSwatch("text-eai-border") },
          ticks: { color: readSwatch("text-eai-ink-subtle"), font: { family: "Inter", size: 10 }, maxTicksLimit: 6 },
        },
        y: {
          grid: { color: readSwatch("text-eai-border") },
          border: { display: false },
          ticks: { color: readSwatch("text-eai-ink-subtle"), font: { family: "Inter", size: 10 }, maxTicksLimit: 5 },
          title: comparison?.unit
            ? {
                display: true,
                text: comparison.unit,
                color: readSwatch("text-eai-ink-subtle"),
                font: { family: "Inter", size: 10 },
              }
            : undefined,
        },
        // Unlabelled and gridless on the right: the extras' shape is the point, their absolute values are not
        yExtra: {
          display: false,
          position: "right" as const,
          grid: { display: false },
        },
      },
    }),
    [comparison, xRange]
  );

  // Logged, not rendered: S3 and AppSync messages carry bucket paths and internal ids the reader can do nothing with
  const failed = Boolean(!comparison && error);
  // The headline only earns its place above real lines — over an empty state it would read as a measured zero
  const hasPoints = Boolean(comparison?.actual.length || comparison?.forecast.length);
  useEffect(() => {
    if (failed) console.error("[GenerationForecastWidget] comparison failed to load:", error);
  }, [failed, error]);

  // One pair is not a choice — the picker only earns the header slot once there is something to switch to
  const picker = onPairChange && pairs.length > 1;

  // A comparison with no overlap still charts, since seeing the lines never meet is the point
  const readings = comparison
    ? comparison.matchedPoints > 0
      ? `${comparison.matchedPoints} compared`
      : "No overlap"
    : null;
  // Counted against the clamped axis, so a source scrolled off the end is never reported as being on screen
  const shown = extrasInWindow(comparison, xRange);
  const offRange = (comparison?.extras.length ?? 0) - shown;
  const extraNote = [shown ? `+${shown} source${shown === 1 ? "" : "s"}` : null, offRange ? `${offRange} off-range` : null]
    .filter(Boolean)
    .join(" · ");
  const drawn = extraNote ? `${readings} · ${extraNote}` : readings;
  // The picker carries the name where there is one; with no comparison there is no meta, as the title already says it
  const description = drawn
    ? picker
      ? drawn
      : `${comparison?.composedForecastName} · ${drawn.toLowerCase()}`
    : undefined;

  return (
    <WidgetFrame
      title="Generation vs forecast"
      meta={description}
      isLoading={false}
      className={className}
      action={
        picker ? (
          <Select
            size="sm"
            aria-label="Which composed forecast to chart"
            value={pairId ?? ""}
            onChange={(event) => onPairChange?.(event.target.value || null)}
            placeholder="Freshest pair"
            options={pairs.map((pair) => ({
              value: pair.composedForecastId,
              label: pair.composedForecastName,
            }))}
            // Narrow until the widget reaches its designed column: at w-48 it took 192 of a phone's 327
            containerClassName="-my-2 w-32 shrink-0 lg:w-48"
          />
        ) : undefined
      }
    >
      {isLoading ? (
        <SkeletonChart withHeader={false} />
      ) : failed ? (
        <EmptyRow>Pair data could not be loaded — try refreshing.</EmptyRow>
      ) : !chartData || !hasPoints ? (
        <EmptyRow>No composed forecast pairs an actual feed with a model output.</EmptyRow>
      ) : (
        // A column, so the plot can take the body's leftover height instead of leaving a gap above the footer rule
        <div className="flex h-full flex-col">
          {headline && (
            // The number and the lines it is read off share one card, so neither can be taken for a figure about something else
            <div className="mb-3 shrink-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                {/* The tile row's display size, kept as it was — this is still the masthead figure, only rehoused */}
                <span
                  className={`font-outfit text-eai-page-title font-semibold leading-none md:text-eai-page-title-lg ${
                    headline.severity === "negative" ? "text-eai-danger" : "text-eai-ink"
                  }`}
                >
                  {headline.value}
                </span>
                {headline.unit && (
                  <span className="whitespace-nowrap font-inter text-eai-body-xs text-eai-ink-subtle">
                    {headline.unit}
                  </span>
                )}
                {headline.delta && (
                  // Direction carries the colour, so the sign is legible without reading the number
                  <span
                    className={`whitespace-nowrap font-outfit text-eai-body-xs font-semibold ${
                      headline.deltaDirection === "up" ? "text-eai-cta-primary" : "text-eai-danger"
                    }`}
                  >
                    {headline.delta}
                  </span>
                )}
              </div>
              {/* The feed reads left, its age on the right edge — the column every card's age sits in */}
              <div className="mt-0.5 flex min-w-0 items-baseline justify-between gap-2 font-inter text-eai-body-xs text-eai-ink-faint">
                <span className="truncate" title={headline.detail}>
                  {headline.detail}
                </span>
                {headline.age && (
                  // shrink-0, so a long feed name truncates and never pushes the age out of sight
                  <span className="shrink-0 whitespace-nowrap">{headline.age}</span>
                )}
              </div>
            </div>
          )}

          {/* Whatever the headline leaves, down to a floor: under lg the card has no fixed height, so flex-1 alone would collapse it */}
          <div className="min-h-[10rem] flex-1">
            <Line data={chartData} options={options} plugins={[nowDivider]} />
          </div>
        </div>
      )}
    </WidgetFrame>
  );
};

export default GenerationForecastWidget;
